import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { format } from 'date-fns';
import { tripAPI } from '../services/api';

const STATUS_LABELS = {
  in_progress: 'في الطريق',
  boarding:    'الصعود',
  scheduled:   'مجدولة',
};
const STATUS_COLORS = {
  in_progress: 'bg-orange-100 text-orange-700',
  boarding:    'bg-yellow-100 text-yellow-700',
  scheduled:   'bg-blue-100 text-blue-700',
};

// Relative time label updated by a tick every 15 s
const useRelativeTime = (isoStr) => {
  const [label, setLabel] = useState('—');

  useEffect(() => {
    const compute = () => {
      if (!isoStr) { setLabel('—'); return; }
      const diffSec = Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000);
      if (diffSec < 10)   setLabel('الآن');
      else if (diffSec < 60)   setLabel(`منذ ${diffSec} ث`);
      else if (diffSec < 3600) setLabel(`منذ ${Math.floor(diffSec / 60)} د`);
      else setLabel(format(new Date(isoStr), 'HH:mm'));
    };
    compute();
    const id = setInterval(compute, 15_000);
    return () => clearInterval(id);
  }, [isoStr]);

  return label;
};

// ── Map panel ────────────────────────────────────────────────────────────────
const MapPanel = ({ trip, loc }) => {
  const relTime = useRelativeTime(loc?.updatedAt);

  if (!trip) {
    return (
      <div className="flex items-center justify-center h-80 text-gray-400 text-sm">
        اختر رحلة من القائمة
      </div>
    );
  }

  const openGoogleMaps = () =>
    window.open(`https://www.google.com/maps?q=${loc.lat},${loc.lng}`, '_blank');

  return (
    <>
      {/* Trip info bar */}
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="font-bold text-gray-800">{trip.fromCity} ← {trip.toCity}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {format(new Date(trip.departureTime), 'yyyy/MM/dd HH:mm')}
            {' · '}{trip.bus?.model || '—'} ({trip.bus?.plateNumber || '—'})
          </p>
        </div>
        {loc && (
          <button
            onClick={openGoogleMaps}
            className="flex items-center gap-1.5 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg border border-blue-200 font-medium transition"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            فتح في Google Maps
          </button>
        )}
      </div>

      {/* Map or waiting */}
      {loc ? (
        <>
          <iframe
            key={`${loc.lat.toFixed(4)}-${loc.lng.toFixed(4)}`}
            title="bus-location"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${loc.lng - 0.06},${loc.lat - 0.04},${loc.lng + 0.06},${loc.lat + 0.04}&layer=mapnik&marker=${loc.lat},${loc.lng}`}
            className="w-full border-0"
            style={{ height: 320 }}
            loading="lazy"
          />
          {/* Stats bar */}
          <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center flex-wrap gap-5 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <span>📍</span>
              <span className="font-mono text-xs">{loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <span>🕐</span>
              <span className="text-xs">{relTime}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-green-700 font-medium">موقع مباشر</span>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 text-gray-400" style={{ height: 320 }}>
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-3xl">📡</div>
          <p className="text-sm font-medium text-gray-500">في انتظار موقع الباص...</p>
          <p className="text-xs text-center px-10 leading-relaxed">
            سيظهر الموقع هنا تلقائياً عندما يبدأ السائق بمشاركة الموقع من صفحة تسجيل الصعود
          </p>
        </div>
      )}
    </>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
const TripTrackingPage = () => {
  const [trips, setTrips]               = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [locations, setLocations]       = useState({}); // { [tripId]: { lat, lng, updatedAt } }
  const [loading, setLoading]           = useState(true);
  const [connected, setConnected]       = useState(false);
  const socketRef = useRef(null);
  const tripsRef  = useRef([]);   // always holds the latest trips list

  // Merge DB locations into state (doesn't overwrite newer socket data)
  const mergeDbLocations = useCallback((tripList) => {
    setLocations((prev) => {
      const next = { ...prev };
      tripList.forEach((t) => {
        const db = t.gpsLocation;
        if (!db?.lat || !db?.lng) return;
        const existing = prev[t.id];
        if (!existing || new Date(db.updatedAt) >= new Date(existing.updatedAt || 0)) {
          next[t.id] = { lat: Number(db.lat), lng: Number(db.lng), updatedAt: db.updatedAt };
        }
      });
      return next;
    });
  }, []);

  // Load active + imminent trips (and preload last known GPS from DB)
  useEffect(() => {
    tripAPI.getCompanyTrips()
      .then((res) => {
        const active = (res.data.data || []).filter(
          (t) => ['in_progress', 'boarding', 'scheduled'].includes(t.status)
        );
        active.sort((a, b) => {
          const order = { in_progress: 0, boarding: 1, scheduled: 2 };
          return (order[a.status] ?? 3) - (order[b.status] ?? 3);
        });
        setTrips(active);
        if (active.length > 0) setSelectedTrip(active[0]);
        mergeDbLocations(active);
      })
      .finally(() => setLoading(false));
  }, [mergeDbLocations]);

  // Poll DB every 15s as fallback (covers bus_host if socket drops)
  useEffect(() => {
    const id = setInterval(() => {
      tripAPI.getCompanyTrips()
        .then((res) => mergeDbLocations(res.data.data || []))
        .catch(() => {});
    }, 15_000);
    return () => clearInterval(id);
  }, [mergeDbLocations]);

  // Keep ref in sync; join rooms if socket already connected
  useEffect(() => {
    tripsRef.current = trips;
    if (socketRef.current?.connected) {
      trips.forEach((t) => socketRef.current.emit('passenger:trackTrip', t.id));
    }
  }, [trips]);

  // Socket.io — connect once, join trip rooms, listen for updates
  useEffect(() => {
    // Bug fix: a plain-string .replace('/api', '') strips the FIRST match of
    // "/api" anywhere in the URL — since the production API host is
    // api.safer-sdn.com, that matched the "api" subdomain instead of the
    // trailing path, producing a garbage host ("https") the socket could
    // never connect to. Anchored regex matches how every other file in this
    // codebase strips the trailing /api segment.
    const serverUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
    // Auth now rides on the httpOnly safer_session cookie (see app.js's
    // socket.io middleware) — there is no client-readable token to attach
    // manually, and there never has been since the cookie migration.
    const socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      // Join every active trip room so we receive trip:locationUpdate for each
      tripsRef.current.forEach((t) => socket.emit('passenger:trackTrip', t.id));
    });

    socket.on('disconnect', () => setConnected(false));

    socket.on('trip:locationUpdate', (data) => {
      setLocations((prev) => ({
        ...prev,
        [data.tripId]: {
          lat: Number(data.lat),
          lng: Number(data.lng),
          updatedAt: data.updatedAt || new Date().toISOString(),
        },
      }));
    });

    return () => socket.disconnect();
  }, []);

  const selectedLoc = selectedTrip ? locations[selectedTrip.id] ?? null : null;
  const liveCount   = Object.keys(locations).length;

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">🗺️ مراقبة مسار الرحلات</h1>
          <p className="text-gray-500 text-sm">متابعة مواقع الباصات في الوقت الفعلي</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {liveCount > 0 && (
            <span className="text-xs bg-green-50 border border-green-200 text-green-700 px-3 py-1.5 rounded-full font-medium">
              📡 {liveCount} باص يبث موقعه
            </span>
          )}
          <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border font-medium ${
            connected
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-gray-50 border-gray-200 text-gray-500'
          }`}>
            <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
            {connected ? 'متصل' : 'جاري الاتصال...'}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">جاري تحميل الرحلات...</div>
      ) : trips.length === 0 ? (
        <div className="text-center bg-white rounded-2xl shadow-sm border border-gray-100 p-16">
          <div className="text-5xl mb-4">🚌</div>
          <p className="font-semibold text-gray-600">لا توجد رحلات نشطة حالياً</p>
          <p className="text-gray-400 text-sm mt-1">تظهر الرحلات هنا عندما تكون في حالة جارية أو صعود</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ── Trips list ── */}
          <div className="lg:col-span-1 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
              الرحلات ({trips.length})
            </p>
            {trips.map((t) => {
              const loc       = locations[t.id];
              const isSelected = selectedTrip?.id === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setSelectedTrip(t)}
                  className={`w-full text-right rounded-xl border p-4 transition-all ${
                    isSelected
                      ? 'bg-blue-50 border-blue-300 shadow-sm'
                      : 'bg-white border-gray-200 hover:border-blue-200 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 text-sm">{t.fromCity} → {t.toCity}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {format(new Date(t.departureTime), 'HH:mm')} · {t.bus?.plateNumber || '—'}
                      </p>
                      {t.bookedSeats?.length > 0 && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {t.bookedSeats.length} حجز
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[t.status] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[t.status] || t.status}
                      </span>
                      {loc ? (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                          مباشر
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">لا موقع</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Legend */}
            <div className="bg-blue-50 rounded-xl border border-blue-100 p-3 text-xs text-blue-700 leading-relaxed mt-2">
              💡 يتحدث الموقع كل 15 ثانية عندما يشغّل السائق خاصية المشاركة من صفحة تسجيل الصعود
            </div>
          </div>

          {/* ── Map panel ── */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow border border-gray-100 overflow-hidden min-h-[420px]">
            <MapPanel trip={selectedTrip} loc={selectedLoc} />
          </div>

        </div>
      )}
    </div>
  );
};

export default TripTrackingPage;
